import React, { useEffect, useState } from "react";
import { Plus, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

/**
 * Dated-remarks dialog. `agent` must have `remarks_log: [{id,date,text}]`
 * (back-compat: if `remarks` string exists and `remarks_log` is empty, surface
 * the legacy string as a single undated entry).
 */
export default function RemarksDialog({ open, onOpenChange, agent, onSave }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open || !agent) return;
    let existing = Array.isArray(agent.remarks_log) ? agent.remarks_log.map((r) => ({ ...r })) : [];
    if (existing.length === 0 && (agent.remarks || "").trim()) {
      existing = [{ id: "legacy", date: "", text: agent.remarks }];
    }
    setItems(existing);
  }, [open, agent]);

  const todayISO = new Date().toISOString().slice(0, 10);

  const addRow = () =>
    setItems((p) => [
      { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`, date: todayISO, text: "" },
      ...p,
    ]);
  const updateRow = (id, k, v) => setItems((p) => p.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const removeRow = (id) => setItems((p) => p.filter((it) => it.id !== id));

  const submit = async () => {
    try {
      const cleaned = items
        .map((r) => ({ id: r.id, date: (r.date || "").trim(), text: (r.text || "").trim() }))
        .filter((r) => r.text);
      await onSave({ remarks_log: cleaned, remarks: cleaned.map((r) => r.text).join(" · ") });
      onOpenChange(false);
      toast.success("Remarks saved");
    } catch {
      toast.error("Failed to save");
    }
  };

  if (!agent) return null;
  const COLS = "150px 1fr 40px";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="remarks-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            Remarks for {agent.agent_name || agent.code || "agent"}
          </DialogTitle>
          <DialogDescription>Log dated notes — newest entries first.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div
            className="grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-3 h-10 items-center bg-slate-100/70 border-b border-slate-200"
            style={{ gridTemplateColumns: COLS }}
          >
            <div>Date</div>
            <div>Remark</div>
            <div></div>
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-slate-400">No remarks yet — click "Add remark".</div>
          ) : (
            items.map((it) => (
              <div
                key={it.id}
                className="grid items-center gap-2 px-3 h-12 border-b border-slate-100 group"
                style={{ gridTemplateColumns: COLS }}
              >
                <Input
                  type="date"
                  value={it.date || ""}
                  onChange={(e) => updateRow(it.id, "date", e.target.value)}
                  className="h-8 text-sm"
                  data-testid={`remark-date-${it.id}`}
                />
                <Input
                  value={it.text}
                  onChange={(e) => updateRow(it.id, "text", e.target.value)}
                  placeholder="Write a remark…"
                  className="h-8 text-sm"
                  data-testid={`remark-text-${it.id}`}
                />
                <button
                  onClick={() => removeRow(it.id)}
                  className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
                  data-testid={`remark-remove-${it.id}`}
                  title="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <div>
          <Button variant="outline" size="sm" onClick={addRow} data-testid="remark-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add remark
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="remark-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
